import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import ChoiceList from "components/ChoiceList";
import HomeSections from "components/HomeSections";
import Skeleton from "components/Skeleton";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { useUser } from "context/UserContext";
import { useFirebase } from "firebase";
import { homeListingPhase, inProgressHome } from "helpers/me-home";
import { useMeHome } from "hooks/useMeHome";

import type { Situation } from "../answers";
import { pickUpCopy } from "../copy";
import { stepPath } from "../destinations";
import {
  PASSWORD_SIGN_IN_PATH,
  readSignupToken,
  type SignInMode,
} from "../signIn";
import type { LeadResult } from "../submit";
import SignedInAs from "./SignedInAs";
import SignInPanel from "./SignInPanel";
import StepFrame from "./StepFrame";
import type { StepProps } from "./types";

const OPTIONS = [
  {
    value: "death_occurred" as const,
    label: "Someone has passed",
    caption: "A death has occurred and you need help with arrangements.",
  },
  {
    value: "planning_ahead" as const,
    label: "I’m planning ahead",
    caption: "For yourself or a loved one.",
  },
];

const LINK_CLASS =
  "font-bold text-evergreen underline underline-offset-4 hover:text-plum";

/** A quiet strip beneath the sign-in line, not a third choice: the stored
 * submission the visitor can pick back up by confirming it. */
function PickUpCard({ result }: { result: LeadResult }) {
  const copy = pickUpCopy(result);
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl bg-oat px-5 py-4">
      <div className="min-w-0">
        <p className="text-lg font-bold leading-snug text-soil">{copy.title}</p>
        <p className="mt-0.5 text-base leading-normal text-oat-700">
          {copy.body}
        </p>
      </div>
      <Link to={stepPath("done")} className={CONTINUE_BUTTON_CLASS}>
        {copy.button}
      </Link>
    </div>
  );
}

function AccountSkeleton() {
  return (
    <div className="mt-6 flex flex-col gap-3" role="status">
      <span className="sr-only">Loading</span>
      <Skeleton className="h-5 w-56" />
      <Skeleton className="h-16 w-full rounded-xl" />
    </div>
  );
}

/** What the person can resume from here: cases and plans still under way,
 * never a finished case or an issued policy. */
function HomeListing() {
  const { home, isLoading } = useMeHome();
  const inProgress = home === null ? null : inProgressHome(home);
  const phase = homeListingPhase(inProgress, isLoading);
  if (phase === "loading") {
    return <Skeleton className="mt-6 h-16 w-full rounded-xl" />;
  }
  if (phase === "empty" || inProgress === null) return null;
  return (
    <div className="mt-6">
      <h2 className="font-serif text-[21px] font-normal text-soil">
        Pick up where you left off
      </h2>
      <div className="mt-3">
        <HomeSections home={inProgress} />
      </div>
    </div>
  );
}

function SignedInBlock({
  account,
  onSignOut,
}: {
  account: StepProps["account"];
  onSignOut: () => void;
}) {
  return (
    <>
      <HomeListing />
      <p className="mt-5 text-base">
        <Link to="/" className={LINK_CLASS}>
          Go to account home
        </Link>
      </p>
      <SignedInAs account={account} onSignOut={onSignOut} className="mt-3" />
    </>
  );
}

function SignInLinks({ onOpen }: { onOpen: (mode: SignInMode) => void }) {
  return (
    <div className="mt-6 flex flex-col gap-1.5 text-base text-oat-700">
      <p>
        Already have an account?{" "}
        <button
          type="button"
          className={LINK_CLASS}
          onClick={() => onOpen("sign_in")}
        >
          Sign in
        </button>
      </p>
      <p>
        Already got a quote with us?{" "}
        <button
          type="button"
          className={LINK_CLASS}
          onClick={() => onOpen("look_up")}
        >
          Look up quote
        </button>
      </p>
    </div>
  );
}

/** What a visitor who is not signed in sees: the two ways in, or the panel
 * one of them opened. A link opened elsewhere that proved a new channel
 * opens the panel at its account step. */
function SignedOutBlock({ pickUp }: Pick<StepProps, "pickUp">) {
  const { state } = useLocation();
  const signupToken = readSignupToken(state);
  const [panel, setPanel] = useState<SignInMode | null>(
    signupToken ? "sign_in" : null,
  );
  return (
    <>
      {panel === null ? (
        <SignInLinks onOpen={setPanel} />
      ) : (
        <>
          <SignInPanel
            mode={panel}
            signupToken={signupToken}
            onCancel={() => setPanel(null)}
          />
          <p className="mt-4 text-base">
            <Link to={PASSWORD_SIGN_IN_PATH} className={LINK_CLASS}>
              Sign in with a password
            </Link>
          </p>
        </>
      )}
      {pickUp && <PickUpCard result={pickUp} />}
    </>
  );
}

/** Below the two first-screen options, which never wait for auth: a
 * placeholder until the visitor is known, then either the way to sign in
 * or the account's own home. */
function AccountArea({
  account,
  onSignOut,
  pickUp,
}: Pick<StepProps, "account" | "onSignOut" | "pickUp">) {
  const { loading } = useUser();
  const { user: firebaseUser } = useFirebase();
  if (loading) return <AccountSkeleton />;
  if (firebaseUser === null) return <SignedOutBlock pickUp={pickUp} />;
  return <SignedInBlock account={account} onSignOut={onSignOut} />;
}

export default function StartStep({
  answers,
  advanceWith,
  account,
  onSignOut,
  onBack,
  pickUp,
}: StepProps) {
  const choose = (situation: Situation) =>
    advanceWith((current) => ({ ...current, situation }));
  return (
    <StepFrame answers={answers} step="start" onBack={onBack}>
      <ChoiceList
        label="How can we help today?"
        options={OPTIONS}
        value={answers.situation}
        onChoose={choose}
      />
      <hr className="mt-8 border-oat-200" />
      <AccountArea account={account} onSignOut={onSignOut} pickUp={pickUp} />
    </StepFrame>
  );
}
